/* ============================================================
   Limiar Studio — Personalizados · experiência 3D
   WebGL puro, sem dependências. Ideia → desenvolvimento → forma.

   Uso: <div data-p3d="hero|solid" data-p3d-variant="a|b|c">
          <img class="p3d__fallback" ...>   (composição estática)
        </div>
   O fallback continua visível até o primeiro quadro renderizado.
   Se WebGL falhar, o dispositivo for fraco ou o usuário preferir
   menos movimento, a página segue funcionando normalmente.
   ============================================================ */
(function () {
  'use strict';

  var doc = document;
  var win = window;
  var reduceMotion = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  var weakDevice = conn.saveData === true ||
    (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);

  if (weakDevice) return;

  /* ---- Geometria (mesma de snippets/personalizados-art e assets/personalizados-forma-*.svg) ---- */
  var VARIANTS = {
    a: { lobes: 3, amp: 0.22, twist: 2.6, waist: 0.04, bulge: 0.1, r: 0.64, h: 2.1, lean: 0.06 },
    b: { lobes: 2, amp: 0.26, twist: 2.8, waist: 0.18, bulge: 0, r: 0.6, h: 1.7, lean: 0 },
    c: { lobes: 4, amp: 0.12, twist: 1.2, waist: 0.05, bulge: 0.12, r: 0.66, h: 1.9, lean: 0 }
  };

  var PI = Math.PI;
  var TAU = PI * 2;

  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function smooth(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
  function ease(x) { x = clamp01(x); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  function shape(p, u, v, out) {
    var e = 0.07;
    var prof = 1 + p.bulge * Math.sin(PI * v) - p.waist * Math.exp(-Math.pow((v - 0.6) / 0.18, 2));
    var top = v > 1 - e ? Math.sqrt(Math.max(0, 1 - Math.pow((v - (1 - e)) / e, 2))) : 1;
    var bot = 0.94 + 0.06 * smooth(v / 0.04);
    var R = p.r * prof * top * bot;
    var a = p.lobes * (u + p.twist * v);
    var rr = R * (1 + p.amp * Math.cos(a) + p.amp * 0.18 * Math.cos(2 * a));
    out[0] = rr * Math.cos(u) + p.lean * Math.sin(PI * v);
    out[1] = (v - 0.5) * p.h;
    out[2] = rr * Math.sin(u);
    return out;
  }

  /* Distribuição de v com mais amostras no ombro superior (curvatura alta). */
  function vAt(j, n) {
    var t = j / n;
    return t < 0.86 ? t * (0.9 / 0.86) : 0.9 + (t - 0.86) / 0.14 * 0.1;
  }

  function buildMesh(p, U, Vn) {
    var verts = [];
    var idx = [];
    var a = [0, 0, 0], b = [0, 0, 0], c = [0, 0, 0], d = [0, 0, 0], q = [0, 0, 0];
    var eps = 0.002;
    var i, j;
    for (j = 0; j <= Vn; j++) {
      var v = vAt(j, Vn);
      for (i = 0; i <= U; i++) {
        var u = (i / U) * TAU;
        shape(p, u, v, q);
        var nx, ny, nz;
        if (j === Vn) {
          nx = 0; ny = 1; nz = 0;
        } else {
          shape(p, u + eps, v, a); shape(p, u - eps, v, b);
          shape(p, u, Math.min(1, v + eps), c); shape(p, u, Math.max(0, v - eps), d);
          var ux = a[0] - b[0], uy = a[1] - b[1], uz = a[2] - b[2];
          var vx = c[0] - d[0], vy = c[1] - d[1], vz = c[2] - d[2];
          nx = vy * uz - vz * uy; ny = vz * ux - vx * uz; nz = vx * uy - vy * ux;
          var cx = q[0] - p.lean * Math.sin(PI * v), cz = q[2];
          if (nx * cx + nz * cz < 0) { nx = -nx; ny = -ny; nz = -nz; }
          var l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
          nx /= l; ny /= l; nz /= l;
        }
        verts.push(q[0], q[1], q[2], nx, ny, nz, v);
      }
    }
    for (j = 0; j < Vn; j++) {
      for (i = 0; i < U; i++) {
        var k0 = j * (U + 1) + i, k1 = k0 + 1, k2 = k0 + U + 1, k3 = k2 + 1;
        idx.push(k0, k2, k1, k1, k2, k3);
      }
    }
    /* base plana */
    var center = verts.length / 7;
    verts.push(0, -p.h / 2, 0, 0, -1, 0, 0);
    for (i = 0; i < U; i++) idx.push(center, i, i + 1);
    return { verts: new Float32Array(verts), idx: new Uint16Array(idx) };
  }

  /* ---- Matrizes (column-major) ---- */
  function perspective(fovy, aspect, near, far) {
    var f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
    return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
  }
  function mul(a, b) {
    var o = new Array(16);
    for (var c = 0; c < 4; c++) {
      for (var r = 0; r < 4; r++) {
        o[c * 4 + r] = a[r] * b[c * 4] + a[4 + r] * b[c * 4 + 1] + a[8 + r] * b[c * 4 + 2] + a[12 + r] * b[c * 4 + 3];
      }
    }
    return o;
  }
  function rotY(t) { var c = Math.cos(t), s = Math.sin(t); return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]; }
  function rotX(t) { var c = Math.cos(t), s = Math.sin(t); return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]; }
  function translate(x, y, z) { return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]; }

  /* ---- Shaders ---- */
  var VS = [
    'attribute vec3 aPos; attribute vec3 aNor; attribute float aV;',
    'uniform mat4 uMVP; uniform mat4 uModel;',
    'varying vec3 vN; varying vec3 vW; varying float vV;',
    'void main(){',
    '  vec4 w = uModel * vec4(aPos, 1.0);',
    '  vW = w.xyz; vV = aV;',
    '  vN = (uModel * vec4(aNor, 0.0)).xyz;',
    '  gl_Position = uMVP * vec4(aPos, 1.0);',
    '}'
  ].join('\n');

  var FS = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'varying vec3 vN; varying vec3 vW; varying float vV;',
    'uniform float uReveal; uniform float uAlpha; uniform float uLayers; uniform float uEdge; uniform vec3 uCam;',
    'void main(){',
    '  if (vV > uReveal) discard;',
    '  vec3 base = vec3(0.905, 0.89, 0.93);',
    '  float edge = smoothstep(0.022, 0.0, uReveal - vV) * uEdge;',
    '  vec3 col;',
    '  if (gl_FrontFacing) {',
    '    vec3 n = normalize(vN);',
    '    vec3 V = normalize(uCam - vW);',
    '    vec3 L1 = normalize(vec3(-0.55, 0.85, 0.65));',
    '    vec3 L2 = normalize(vec3(0.9, 0.15, -0.35));',
    '    float d1 = max(dot(n, L1), 0.0);',
    '    float d2 = max(dot(n, L2), 0.0);',
    '    float hemi = 0.5 + 0.5 * n.y;',
    '    vec3 amb = mix(vec3(0.09, 0.085, 0.19), vec3(0.24, 0.23, 0.32), hemi);',
    '    float wrap = max((dot(n, L1) + 0.25) / 1.25, 0.0);',
    '    col = base * (amb + pow(wrap, 1.6) * vec3(1.0, 0.97, 0.94) * 0.86 + d2 * vec3(0.5, 0.42, 0.95) * 0.3);',
    '    float rim = pow(1.0 - max(dot(n, V), 0.0), 3.0);',
    '    col += rim * vec3(0.52, 0.36, 0.86) * 0.16;',
    '    float l = abs(fract(vV * uLayers) - 0.5) * 2.0;',
    '    col *= 0.955 + 0.045 * smoothstep(0.15, 0.85, l);',
    '  } else {',
    '    col = base * vec3(0.8, 0.78, 0.86);',
    '  }',
    '  col = mix(col, vec3(0.82, 0.7, 1.0), edge * 0.55);',
    '  gl_FragColor = vec4(col * uAlpha, uAlpha);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }

  /* ---- Rabiscos ---- */
  function hash(i, n) { var x = Math.sin((i + 1) * 12.9898 + n * 78.233) * 43758.5453; return x - Math.floor(x); }

  function makeStroke(i) {
    return {
      c: [(hash(i, 1) - 0.5) * 1.5, (hash(i, 2) - 0.5) * 1.8, (hash(i, 3) - 0.5) * 1.1],
      A: 0.32 + hash(i, 4) * 0.28,
      f1: 0.8 + hash(i, 5) * 1.3, f2: 0.7 + hash(i, 6) * 1.4, f3: 0.6 + hash(i, 7) * 1.2,
      ph: hash(i, 8) * TAU,
      w: 0.18 + hash(i, 9) * 0.2
    };
  }

  function scribble(s, t, time, out) {
    var q = t * TAU, tt = time * s.w;
    out[0] = s.c[0] + s.A * (Math.sin(s.f1 * q + s.ph + tt) + 0.35 * Math.sin(s.f2 * 2.3 * q + tt * 0.7));
    out[1] = s.c[1] + s.A * (Math.sin(s.f2 * q + s.ph * 1.3 + tt * 0.8) + 0.3 * Math.cos(s.f3 * 2.1 * q - tt * 0.5));
    out[2] = s.c[2] + s.A * Math.sin(s.f3 * q + s.ph * 0.7 + tt * 0.6);
    return out;
  }

  /* ---- Linha do tempo (loop de 16s, sem costura) ---- */
  var LOOP = 16;
  function timeline(t) {
    function s(a, b) { return smooth((t - a) / (b - a)); }
    return {
      morph: ease((t - 2.2) / 3.2) - ease((t - 14.2) / 1.8),
      strokes: 1 - s(8.4, 10.2) + s(13.2, 14.2),
      growth: s(6.2, 9.2),
      rings: s(6, 6.4) * (1 - s(10, 11.2)) + s(12.8, 13.3) * (1 - s(14.6, 15.4)),
      reveal: s(6.6, 9.8) * (1 - s(13, 14.6)),
      edge: s(6.6, 7) * (1 - s(9.4, 9.9)) + s(13, 13.3) * (1 - s(14.3, 14.6)),
      speed: 0.16 + 0.08 * s(9.8, 10.8) * (1 - s(12.4, 13.2))
    };
  }

  /* ---- Cena ---- */
  function Scene(el) {
    this.el = el;
    this.mode = el.getAttribute('data-p3d') === 'hero' ? 'hero' : 'solid';
    this.p = VARIANTS[el.getAttribute('data-p3d-variant')] || VARIANTS.a;
    this.small = win.matchMedia('(max-width: 749px)').matches;
    this.tablet = !this.small && win.matchMedia('(max-width: 1023px)').matches;
    this.fine = win.matchMedia('(hover: hover) and (pointer: fine)').matches && !this.small;
    this.time = this.mode === 'hero' ? 0 : 11;
    this.yaw = parseFloat(el.getAttribute('data-p3d-yaw') || '0.5');
    this.tilt = [0, 0];
    this.tiltTarget = [0, 0];
    this.scroll = 0;
    this.running = false;
    this.frames = 0;
    this.slow = 0;
    this.last = 0;
    this.ready = false;
    this.tmp = [0, 0, 0];
    this.clock = 0;
    this.accent = '#8A34C8';
  }

  Scene.prototype.init = function () {
    var el = this.el;
    var gl;
    this.glCanvas = doc.createElement('canvas');
    this.glCanvas.className = 'p3d__canvas';
    this.glCanvas.setAttribute('aria-hidden', 'true');
    try {
      gl = this.glCanvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true, powerPreference: 'low-power' });
    } catch (e) { gl = null; }
    if (!gl) return false;
    this.gl = gl;

    try {
      var prog = gl.createProgram();
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
      this.prog = prog;
    } catch (e) { return false; }

    var U = this.small ? 56 : this.tablet ? 72 : 96;
    var Vn = this.small ? 64 : this.tablet ? 84 : 120;
    var mesh = buildMesh(this.p, U, Vn);
    this.count = mesh.idx.length;
    this.vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, mesh.verts, gl.STATIC_DRAW);
    this.ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.idx, gl.STATIC_DRAW);

    var P = this.prog;
    this.loc = {
      pos: gl.getAttribLocation(P, 'aPos'), nor: gl.getAttribLocation(P, 'aNor'), v: gl.getAttribLocation(P, 'aV'),
      mvp: gl.getUniformLocation(P, 'uMVP'), model: gl.getUniformLocation(P, 'uModel'),
      reveal: gl.getUniformLocation(P, 'uReveal'), alpha: gl.getUniformLocation(P, 'uAlpha'),
      layers: gl.getUniformLocation(P, 'uLayers'), edge: gl.getUniformLocation(P, 'uEdge'), cam: gl.getUniformLocation(P, 'uCam')
    };

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    var self = this;
    this.glCanvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); self.fail(); });

    this.shadow = doc.createElement('span');
    this.shadow.className = 'p3d__shadow';
    this.shadow.setAttribute('aria-hidden', 'true');
    el.appendChild(this.shadow);
    el.appendChild(this.glCanvas);

    if (this.mode === 'hero') {
      this.lineCanvas = doc.createElement('canvas');
      this.lineCanvas.className = 'p3d__canvas p3d__canvas--lines';
      this.lineCanvas.setAttribute('aria-hidden', 'true');
      el.appendChild(this.lineCanvas);
      this.ctx = this.lineCanvas.getContext('2d');
      this.buildLines();
    }

    this.resize();
    if (win.ResizeObserver) {
      this.ro = new ResizeObserver(function () { self.resize(); self.drawOnce(); });
      this.ro.observe(el);
    }
    return true;
  };

  Scene.prototype.buildLines = function () {
    var p = this.p;
    var meridians = this.small ? 8 : 10;
    var rings = this.small ? 5 : 6;
    var N = this.small ? 32 : 44;
    var strokes = [];
    var i, j, k;
    for (i = 0; i < meridians + rings; i++) {
      var s = makeStroke(i);
      s.target = [];
      s.us = [];
      var isRing = i >= meridians;
      for (j = 0; j <= N; j++) {
        var t = j / N, u, v;
        if (isRing) { v = (i - meridians + 1) / (rings + 1); u = t * TAU; } else { u = (i / meridians) * TAU; v = t * 0.99; }
        s.target.push(shape(p, u, v, [0, 0, 0]));
        s.us.push(u);
      }
      s.accent = i === 2 || i === meridians + 2;
      strokes.push(s);
    }
    this.strokes = strokes;
    this.N = N;

    /* camadas (anéis de impressão) */
    var L = this.small ? 26 : 40;
    var K = this.small ? 40 : 56;
    this.layers = [];
    for (k = 0; k < L; k++) {
      var vv = (k + 0.5) / L * 0.985;
      var ring = [];
      for (j = 0; j <= K; j++) ring.push(shape(p, (j / K) * TAU, vv, [0, 0, 0]));
      this.layers.push({ v: vv, pts: ring });
    }

    /* pontos soltos que viram vértices */
    this.dots = [];
    var D = this.small ? 14 : 26;
    for (i = 0; i < D; i++) {
      var du = Math.floor(hash(i, 11) * meridians) / meridians * TAU;
      var dv = (Math.floor(hash(i, 12) * rings) + 1) / (rings + 1);
      this.dots.push({ s: makeStroke(40 + i), target: shape(p, du, dv, [0, 0, 0]), u: du });
    }
  };

  Scene.prototype.resize = function () {
    var r = this.el.getBoundingClientRect();
    var dprMax = this.small ? 1.5 : 2;
    var dpr = Math.min(win.devicePixelRatio || 1, dprMax);
    this.w = Math.max(1, Math.round(r.width));
    this.h = Math.max(1, Math.round(r.height));
    this.dpr = dpr;
    var W = Math.round(this.w * dpr), H = Math.round(this.h * dpr);
    this.glCanvas.width = W; this.glCanvas.height = H;
    if (this.lineCanvas) {
      this.lineCanvas.width = W; this.lineCanvas.height = H;
      var g = this.ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#4467FD'); g.addColorStop(0.5, '#8A34C8'); g.addColorStop(1, '#E41D8E');
      this.accent = g;
    }
    if (this.gl) this.gl.viewport(0, 0, W, H);
  };

  Scene.prototype.matrices = function () {
    var aspect = this.w / this.h;
    var fov = 30 * PI / 180;
    var tf = Math.tan(fov / 2);
    var halfH = this.mode === 'hero' ? 1.42 : 1.2;
    var halfW = this.mode === 'hero' ? 1.3 : 0.95;
    var dist = Math.max(halfH / tf, halfW / (tf * aspect)) + 1.0;
    var pitch = 0.3 + this.tilt[0] + this.scroll * 0.14;
    var yawOffset = this.tilt[1];
    var model = mul(rotX(pitch), rotY(this.yaw + yawOffset));
    var view = translate(0, -0.02 - this.scroll * 0.08, -dist);
    var proj = perspective(fov, aspect, 0.1, 50);
    this.model = model;
    this.mvp = mul(proj, mul(view, model));
    this.dist = dist;
    /* câmera no espaço "mundo" após o modelo: fica em (0, y, dist) */
    this.cam = [0, 0.02 + this.scroll * 0.08, dist];
  };

  Scene.prototype.project = function (x, y, z, out) {
    var m = this.mvp;
    var cx = m[0] * x + m[4] * y + m[8] * z + m[12];
    var cy = m[1] * x + m[5] * y + m[9] * z + m[13];
    var cw = m[3] * x + m[7] * y + m[11] * z + m[15];
    out[0] = (cx / cw * 0.5 + 0.5) * this.w * this.dpr;
    out[1] = (0.5 - cy / cw * 0.5) * this.h * this.dpr;
    out[2] = cw;
    return out;
  };

  /* face voltada para a câmera? usa a normal radial no ângulo u */
  Scene.prototype.facing = function (u) {
    var m = this.model;
    var nx = Math.cos(u), nz = Math.sin(u);
    return m[2] * nx + m[10] * nz;
  };

  Scene.prototype.drawLines = function (tl) {
    var ctx = this.ctx;
    var dpr = this.dpr;
    ctx.clearRect(0, 0, this.lineCanvas.width, this.lineCanvas.height);
    var strokesA = clamp01(tl.strokes);
    var ringsA = clamp01(tl.rings);
    var pt = this.tmp, q = [0, 0, 0];
    var time = this.clock;
    var i, j, k;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (strokesA > 0.01) {
      var S = this.strokes.length;
      for (i = 0; i < S; i++) {
        var s = this.strokes[i];
        var mi = ease(tl.morph * 1.5 - (i / S) * 0.5);
        var front = new Path2D(), back = new Path2D();
        var prevF = null;
        for (j = 0; j <= this.N; j++) {
          scribble(s, j / this.N, time, q);
          var tg = s.target[j];
          var x = lerp(q[0], tg[0], mi), y = lerp(q[1], tg[1], mi), z = lerp(q[2], tg[2], mi);
          this.project(x, y, z, pt);
          var isFront = mi < 0.5 || this.facing(s.us[j]) > -0.05;
          var path = isFront ? front : back;
          if (j === 0 || prevF !== isFront) path.moveTo(pt[0], pt[1]); else path.lineTo(pt[0], pt[1]);
          prevF = isFront;
        }
        var baseA = (s.accent ? 0.85 : 0.38) * strokesA;
        var solidCover = clamp01(tl.reveal * 1.6);
        ctx.lineWidth = (s.accent ? 1.4 : 1.1) * dpr;
        ctx.strokeStyle = s.accent ? this.accent : 'rgba(243,241,245,1)';
        ctx.globalAlpha = baseA;
        ctx.stroke(front);
        ctx.globalAlpha = baseA * lerp(1, 0.28, mi) * (1 - solidCover);
        ctx.stroke(back);
      }

      /* pontos */
      ctx.fillStyle = 'rgba(243,241,245,1)';
      for (i = 0; i < this.dots.length; i++) {
        var d = this.dots[i];
        var md = ease(tl.morph * 1.3 - (i / this.dots.length) * 0.3);
        scribble(d.s, 0.3, time, q);
        this.project(lerp(q[0], d.target[0], md), lerp(q[1], d.target[1], md), lerp(q[2], d.target[2], md), pt);
        var fa = md > 0.5 && this.facing(d.u) < -0.05 ? 0.25 * (1 - clamp01(tl.reveal * 1.6)) : 0.75;
        ctx.globalAlpha = strokesA * fa;
        ctx.beginPath();
        ctx.arc(pt[0], pt[1], 1.5 * dpr, 0, TAU);
        ctx.fill();
      }
    }

    if (ringsA > 0.01) {
      var L = this.layers.length;
      ctx.lineWidth = 1 * dpr;
      ctx.strokeStyle = 'rgba(243,241,245,1)';
      for (k = 0; k < L; k++) {
        var layer = this.layers[k];
        var born = clamp01((tl.growth - k / L) * L * 0.6);
        if (born <= 0) continue;
        var covered = layer.v < tl.reveal;
        var head = tl.growth < 1 && (k / L) > tl.growth - 1.5 / L;
        var fp = new Path2D(), bp = new Path2D();
        var K = layer.pts.length - 1;
        var prev = null;
        for (j = 0; j <= K; j++) {
          var P = layer.pts[j];
          this.project(P[0], P[1], P[2], pt);
          var f = this.facing((j / K) * TAU) > -0.02;
          var pa = f ? fp : bp;
          if (j === 0 || prev !== f) pa.moveTo(pt[0], pt[1]); else pa.lineTo(pt[0], pt[1]);
          prev = f;
        }
        var a = ringsA * born * (covered ? 0.22 : head ? 0.95 : 0.55);
        ctx.strokeStyle = head ? this.accent : 'rgba(243,241,245,1)';
        ctx.globalAlpha = a;
        ctx.stroke(fp);
        if (!covered) { ctx.globalAlpha = a * 0.3; ctx.stroke(bp); }
      }
    }
    ctx.globalAlpha = 1;
  };

  Scene.prototype.drawSolid = function (tl) {
    var gl = this.gl, L = this.loc;
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    if (tl.reveal <= 0.001) return;
    gl.useProgram(this.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.enableVertexAttribArray(L.pos); gl.vertexAttribPointer(L.pos, 3, gl.FLOAT, false, 28, 0);
    gl.enableVertexAttribArray(L.nor); gl.vertexAttribPointer(L.nor, 3, gl.FLOAT, false, 28, 12);
    gl.enableVertexAttribArray(L.v); gl.vertexAttribPointer(L.v, 1, gl.FLOAT, false, 28, 24);
    gl.uniformMatrix4fv(L.mvp, false, this.mvp);
    gl.uniformMatrix4fv(L.model, false, this.model);
    gl.uniform1f(L.reveal, tl.reveal >= 0.999 ? 1.01 : tl.reveal);
    gl.uniform1f(L.alpha, 1);
    gl.uniform1f(L.layers, this.small ? 70 : 110);
    gl.uniform1f(L.edge, tl.edge);
    gl.uniform3fv(L.cam, this.cam);
    gl.drawElements(gl.TRIANGLES, this.count, gl.UNSIGNED_SHORT, 0);
  };

  Scene.prototype.state = function () {
    if (this.mode !== 'hero') return { reveal: 1, edge: 0, speed: 0.12, strokes: 0, rings: 0, growth: 1, morph: 1 };
    if (reduceMotion) return { reveal: 1, edge: 0, speed: 0, strokes: 0, rings: 0, growth: 1, morph: 1 };
    return timeline(this.time % LOOP);
  };

  Scene.prototype.render = function () {
    var tl = this.state();
    this.matrices();
    this.drawSolid(tl);
    if (this.ctx) this.drawLines(tl);
    this.shadow.style.opacity = (0.15 + 0.85 * smooth(tl.reveal * 1.4)).toFixed(3);
    if (!this.ready) {
      this.ready = true;
      this.el.classList.add('is-3d-ready');
    }
    return tl;
  };

  Scene.prototype.drawOnce = function () { if (this.gl && !this.running) this.render(); };

  Scene.prototype.frame = function (now) {
    if (!this.running) return;
    var self = this;
    var dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 0.016;
    var raw = this.last ? now - this.last : 16;
    this.last = now;

    /* detecção de baixa performance nos primeiros quadros */
    this.frames++;
    if (this.frames > 20 && this.frames < 140 && raw > 48) this.slow++;
    if (this.frames === 140 && this.slow > 60) { this.fail(); return; }

    this.time += dt;
    this.clock += dt;
    this.tilt[0] = lerp(this.tilt[0], this.tiltTarget[0], 0.04);
    this.tilt[1] = lerp(this.tilt[1], this.tiltTarget[1], 0.04);
    var tl = this.render();
    this.yaw += dt * tl.speed * (this.small ? 0.8 : 1);
    this.raf = win.requestAnimationFrame(function (t) { self.frame(t); });
  };

  Scene.prototype.start = function () {
    if (this.running || reduceMotion || this.failed) return;
    var self = this;
    this.running = true;
    this.last = 0;
    this.raf = win.requestAnimationFrame(function (t) { self.frame(t); });
  };

  Scene.prototype.stop = function () {
    this.running = false;
    if (this.raf) win.cancelAnimationFrame(this.raf);
  };

  Scene.prototype.fail = function () {
    this.stop();
    this.failed = true;
    this.el.classList.remove('is-3d-ready');
    this.el.classList.add('is-3d-off');
    if (this.ro) this.ro.disconnect();
    var nodes = [this.glCanvas, this.lineCanvas, this.shadow];
    for (var i = 0; i < nodes.length; i++) if (nodes[i] && nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
  };

  /* ---- Interação: cursor (desktop) e scroll ---- */
  function bindPointer(scene) {
    if (!scene.fine || reduceMotion) return;
    var max = 3.5 * PI / 180;
    win.addEventListener('pointermove', function (e) {
      var nx = e.clientX / win.innerWidth - 0.5;
      var ny = e.clientY / win.innerHeight - 0.5;
      scene.tiltTarget[0] = ny * max * 2 * 0.6;
      scene.tiltTarget[1] = nx * max * 2;
    }, { passive: true });
  }

  function bindScroll(scene) {
    if (scene.small || reduceMotion) return;
    var ticking = false;
    function update() {
      ticking = false;
      var r = scene.el.getBoundingClientRect();
      scene.scroll = clamp01(-r.top / Math.max(1, r.height));
    }
    win.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; win.requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function mount(el) {
    var scene = new Scene(el);
    var ok = false;
    try { ok = scene.init(); } catch (e) { ok = false; }
    if (!ok) { el.classList.add('is-3d-off'); return; }
    bindPointer(scene);
    bindScroll(scene);
    scene.render();

    if ('IntersectionObserver' in win) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) scene.start(); else scene.stop(); });
      }, { threshold: 0.01 }).observe(el);
    } else {
      scene.start();
    }
    doc.addEventListener('visibilitychange', function () {
      if (doc.hidden) scene.stop();
      else if (el.getBoundingClientRect().bottom > 0 && el.getBoundingClientRect().top < win.innerHeight) scene.start();
    });
  }

  function boot() {
    var nodes = doc.querySelectorAll('[data-p3d]');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in win)) { for (var i = 0; i < nodes.length; i++) mount(nodes[i]); return; }
    /* só inicializa cada cena quando ela se aproxima da viewport */
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        obs.unobserve(en.target);
        mount(en.target);
      });
    }, { rootMargin: '300px 0px' });
    for (var j = 0; j < nodes.length; j++) io.observe(nodes[j]);
  }

  function idle(fn) {
    if ('requestIdleCallback' in win) win.requestIdleCallback(fn, { timeout: 1500 });
    else win.setTimeout(fn, 200);
  }

  if (doc.readyState === 'complete') idle(boot);
  else win.addEventListener('load', function () { idle(boot); });
})();
